'use client'

import { MoonIcon, SunIcon } from '@radix-ui/react-icons'
import { Bug, CalendarDays, Gift, MoreHorizontal, Search, SmilePlus } from 'lucide-react'
import Link from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'
import { useTheme } from 'next-themes'
import * as React from 'react'

import { FeedbackDialog } from '@/components/feedback-dialog'
import { SentryFeedbackTrigger } from '@/components/sentry-feedback-trigger'
import { requestOpenGlobalNavCommand } from '@/components/sidebar/global-nav-command-events'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { useNewsModal } from '@/contexts/NewsModalContext'
import { useTopNavTitle } from '@/contexts/TopNavTitleContext'
import { appendWorkspaceScopeToPath } from '@/utils/workspace-scope'

function getTitleFromPathname(pathname: string): string {
  const p = pathname || '/'

  if (p.startsWith('/v2/new-agent')) return 'New Agent'
  if (p.startsWith('/new-agent')) return 'New Agent'
  if (p.startsWith('/agent-ide/session/')) return 'Agent IDE'
  if (p.startsWith('/agent')) return 'Agent'
  if (p.startsWith('/apps')) return 'Apps'
  if (p.startsWith('/workflows') || p.startsWith('/workflow')) return 'Workflows'
  if (p.startsWith('/projects') || p.startsWith('/project')) return 'Projects'

  if (p.startsWith('/fold')) return 'Fold'
  if (p.startsWith('/openmm')) return 'MD Simulation'
  if (p.startsWith('/design')) return 'Design'
  if (p.startsWith('/properties')) return 'Properties'

  if (p.startsWith('/library')) return 'Library'
  if (p.startsWith('/jobs')) return 'Jobs'
  if (p.startsWith('/usage')) return 'Usage'
  if (p.startsWith('/invoices')) return 'Invoices'
  if (p.startsWith('/api-keys')) return 'API Keys'
  if (p.startsWith('/models')) return 'Models'
  if (p.startsWith('/integrations')) return 'Integrations'
  if (p.startsWith('/evolla')) return 'Evolla Protein Chat'

  if (p.startsWith('/mol')) return 'Mol* Nano Protein'
  if (p.startsWith('/py2dmol')) return 'Py2DMol'
  if (p.startsWith('/code')) return 'Code Viewer'

  if (p.startsWith('/settings')) return 'Settings'
  if (/^\/teams\/[^/]+\/settings(?:\/|$)/.test(p)) return 'Team Settings'
  if (/^\/teams\/[^/]+\/members(?:\/|$)/.test(p)) return 'Team Members'
  if (p.startsWith('/login')) return 'Login'
  if (p.startsWith('/signup')) return 'Sign up'

  if (p === '/') return 'Home'

  return 'FastFold'
}

export function SharedTopNav({
  title,
  left,
  right,
  hideSearch = false,
}: {
  title?: string
  left?: React.ReactNode
  right?: React.ReactNode
  /** Hide command search (e.g. anonymous public share pages). */
  hideSearch?: boolean
}) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { openModal } = useNewsModal()
  const { title: ctxTitle, titleLeftHref: ctxTitleLeftHref } = useTopNavTitle()
  const { resolvedTheme, setTheme } = useTheme()
  const [feedbackDialogOpen, setFeedbackDialogOpen] = React.useState(false)
  const [reportBugDialogOpen, setReportBugDialogOpen] = React.useState(false)
  const [themeReady, setThemeReady] = React.useState(false)
  React.useEffect(() => {
    setThemeReady(true)
  }, [])
  const isDark = themeReady && resolvedTheme === 'dark'
  const computedTitle = title ?? ctxTitle ?? getTitleFromPathname(String(pathname || ''))
  const shortcutModifier = React.useMemo(() => {
    if (typeof navigator === 'undefined') return '⌘'
    return /Mac|iPhone|iPad|iPod/i.test(navigator.platform) ? '⌘' : 'Ctrl'
  }, [])
  const showWideSearch = !right
  const titleParts = React.useMemo(() => {
    const t = String(computedTitle || '')
    const idx = t.indexOf(' / ')
    if (idx === -1) return null
    return {
      left: t.slice(0, idx),
      right: t.slice(idx + 3)
    }
  }, [computedTitle])
  const breadcrumbHrefs = React.useMemo(() => {
    const p = String(pathname || '')
    const activeTeamId = searchParams.get('team_id')
    // Use explicit left href from context when set (e.g. project name -> /projects/<id>).
    if (ctxTitleLeftHref) {
      return {
        leftHref: appendWorkspaceScopeToPath(ctxTitleLeftHref, activeTeamId),
        rightHref: appendWorkspaceScopeToPath(p || '/', activeTeamId),
      }
    }
    // Default: link left to the "section root" when we can infer it.
    const leftLabel = titleParts?.left
    const leftHref =
      leftLabel === 'Jobs'
        ? '/jobs'
        : leftLabel === 'Projects'
          ? '/projects'
          : leftLabel === 'Library'
            ? '/library'
            : leftLabel === 'Integrations'
              ? '/integrations'
            : leftLabel === 'Workflows'
              ? '/workflows'
              : leftLabel === 'Apps'
                ? '/apps'
              : leftLabel === 'Design'
                ? '/design'
                : leftLabel === 'Fold'
                  ? '/fold'
                  : leftLabel === 'Properties'
                    ? '/properties'
                    : p.startsWith('/agent')
                      ? '/agent'
                      : '/'
    const rightHref = appendWorkspaceScopeToPath(p || '/', activeTeamId)
    const scopedLeftHref = appendWorkspaceScopeToPath(leftHref, activeTeamId)
    return { leftHref: scopedLeftHref, rightHref }
  }, [pathname, titleParts?.left, ctxTitleLeftHref, searchParams])

  return (
    <header className="z-50 h-12 shrink-0 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="grid h-full grid-cols-[1fr_auto_1fr] items-center px-2 md:px-4">
        <div className="flex items-center gap-2">{left}</div>
        <div className="min-w-0 max-w-full overflow-hidden text-sm font-medium text-foreground">
          {titleParts ? (
            <div className="flex min-w-0 max-w-[60vw] items-center justify-center gap-0">
              <Link
                href={breadcrumbHrefs.leftHref}
                className="min-w-0 shrink truncate text-muted-foreground hover:text-foreground"
              >
                {titleParts.left}
              </Link>
              <span className="mx-2 shrink-0 text-muted-foreground">/</span>
              <span
                key={titleParts.right}
                className="min-w-0 truncate text-foreground animate-in fade-in slide-in-from-bottom-1 duration-200"
              >
                {titleParts.right}
              </span>
            </div>
          ) : (
            <span className="block max-w-[60vw] truncate">{computedTitle}</span>
          )}
        </div>
        <div className="flex items-center justify-end gap-2">
          {!hideSearch ? (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={requestOpenGlobalNavCommand}
                className={[
                  'h-9 justify-start rounded-xl border border-border/60 bg-background/70 px-3 text-muted-foreground hover:bg-accent/40',
                  showWideSearch ? 'hidden md:flex md:min-w-[260px]' : 'hidden lg:flex lg:min-w-[220px] xl:min-w-[260px]'
                ].join(' ')}
                aria-label="Open command search"
              >
                <Search className="mr-2 h-4 w-4 shrink-0" />
                <span className="mr-auto text-sm">Search</span>
                <span className="ml-2 flex items-center gap-1">
                  <kbd className="rounded-md bg-background/70 px-1.5 py-0.5 font-mono text-[11px] leading-none">
                    {shortcutModifier}
                  </kbd>
                  <kbd className="rounded-md bg-background/70 px-1.5 py-0.5 font-mono text-[11px] leading-none">
                    G
                  </kbd>
                </span>
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={requestOpenGlobalNavCommand}
                className={showWideSearch ? 'md:hidden' : 'lg:hidden'}
                aria-label="Open command search"
              >
                <Search className="h-5 w-5" />
              </Button>
            </>
          ) : null}
          {hideSearch ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="relative"
              onClick={() => setTheme(isDark ? 'light' : 'dark')}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDark ? 'Light mode' : 'Dark mode'}
            >
              <SunIcon className="h-[1.1rem] w-[1.1rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
              <MoonIcon className="absolute h-[1.1rem] w-[1.1rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
            </Button>
          ) : null}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu">
                <MoreHorizontal className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-44">
              <DropdownMenuItem onSelect={openModal}>
                <Gift className="mr-2 h-4 w-4" />
                What&apos;s New
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setFeedbackDialogOpen(true)}>
                <SmilePlus className="mr-2 h-4 w-4" />
                Give Feedback
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setReportBugDialogOpen(true)}>
                <Bug className="mr-2 h-4 w-4" />
                Report Bug
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => {
                  window.open('https://calendly.com/julio-fastfold/30min', '_blank', 'noopener,noreferrer')
                }}
              >
                <CalendarDays className="mr-2 h-4 w-4" />
                Book a meeting
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <FeedbackDialog
            open={feedbackDialogOpen}
            onOpenChange={setFeedbackDialogOpen}
            hideTrigger
          />
          <SentryFeedbackTrigger
            open={reportBugDialogOpen}
            onOpenChange={setReportBugDialogOpen}
            hideTrigger
          />
          {right}
        </div>
      </div>
    </header>
  )
}

